import { ArpMediaType } from '@arpeggi/shared'
import { observer } from 'mobx-react'
import { FC, ReactElement, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArpTable } from '../../../../../features/arp-table'
import { ArpMediaFilters } from '../../../../../features/arp-table/hooks'
import { TableRow } from '../../../../../features/arp-tablerow'
import { styled } from '../../../../lib/stitches'
import { useTreeState } from '../../../../stores/Root'
import Button from '../../../core/Button/Button'
import { Box } from '../../../core/Components'
import { segmentTrackRemix } from '../../../core/Utils'
import { PreviewPlayer } from '../../explore/common/MediaPreview'
import { useRemix } from '../../explore/common/utils'
import Spinner from './Spinner'
import { TableRowSkeleton } from './TableRow'

const segmentClearButton = () => {
  window.analytics.track('Button Clicked', {
    button: 'clear filters',
    location: 'explore page'
  })
}

const segmentDetails = (type, id) => {
  window.analytics.track('Button Clicked', {
    button: 'see details',
    source: window.location.pathname,
    location: 'token',
    type: type,
    id: id
  })
}

export const TableSkeleton: FC<{ size: number }> = ({ size }) => {
  const arr = useMemo(() => new Array(size).fill(true), [size])
  return (
    <>
      {arr.map((_, i) => (
        <TableRowSkeleton key={i} />
      ))}
    </>
  )
}

export const Loader: FC = () => (
  <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px' }}>
    <Spinner />
  </Box>
)

export const PreviewBar: FC<{ id: number; type: ArpMediaType; title?: string }> = ({
  id,
  type,
  title
}) => {
  const { remixSong, remixStem, remixSample, status } = useRemix()
  const navigate = useNavigate()
  const handleRemix = (evt) => {
    evt.preventDefault()
    segmentTrackRemix(type, id, 'token')
    if (type === ArpMediaType.SONG) remixSong(id)
    if (type === ArpMediaType.SAMPLE) remixSample(id)
    if (type === ArpMediaType.STEM) remixStem(id)
  }
  const handleDetails = () => {
    segmentDetails(type, id)
    navigate(`/${type}?token=${id}`)
  }
  return (
    <Box
      css={{
        borderLeft: 'rgba(0,0,0,.1) 1px solid',
        flexBasis: '400px',
        padding: '24px',
        display: 'none',
        minHeight: '100vh',
        boxSizing: 'border-box',
        '@laptop15inch': { flexBasis: '500px' },
        '@tablet': { display: 'block' }
      }}
    >
      <Box css={{ position: 'sticky', top: '110px' }}>
        <PreviewPlayer type={type} index={id} autoPlay={true} blankStateTitle={title} />
        {id !== -1 && (
          <Box css={{ display: 'flex', marginTop: '16px' }}>
            <Button big type={'outlined'} css={{ margin: '5px', flex: 1 }} onClick={handleDetails}>
              {'See details'}
            </Button>
            <Button
              disabled={status === 'loading'}
              big
              type={'dark'}
              css={{ flex: 1, margin: '5px' }}
              onClick={handleRemix}
            >
              {status === 'ready' ? 'Open in Studio' : <Spinner />}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

const StyledFilterContainer = styled('div', {
  maxWidth: '100vw',
  paddingTop: '16px',
  paddingBottom: '24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxSizing: 'border-box',
  borderBottomWidth: '1px',
  borderBottomColor: '$gray02',
  borderBottomStyle: 'solid',
  '@tablet': {
    borderBottomWidth: '0',
    paddingTop: '16px',
    paddingBottom: '$16'
  }
})

const StyledStickyNavigation = styled('div', {
  zIndex: 1
})

interface TableProps {
  isSticky?: boolean
  top?: string
  defaultFilters?: ArpMediaFilters
  emptyState?: ReactElement
}

const { Filters, Headers, DEFAULT_SOUNDS_FILTERS, DEFAULT_SONGS_FILTERS } = ArpTable

export const SoundsTable: FC<TableProps> = observer(
  ({ isSticky, top, defaultFilters, emptyState }) => {
    const { globalPlayer } = useTreeState()
    const currentId = globalPlayer.arpMediaIndex
    return (
      <ArpTable.Root defaultFilters={defaultFilters || DEFAULT_SOUNDS_FILTERS}>
        <Filters.Root tags>
          <Filters.Instrument />
          <Filters.Key />
          <Filters.Type />
        </Filters.Root>
        <Headers.Root>
          <Headers.Title />
          <Headers.Tags />
          <Headers.DateMinted />
          <Headers.Remixes />
          <Headers.Favorite />
          <Headers.Duration />
        </Headers.Root>
        <ArpTable.Table>
          {(media) => (
            <TableRow.Root media={media} key={media.id} active={currentId === media.id}>
              <TableRow.Main />
              <TableRow.Tags />
              <TableRow.DateMinted />
              <TableRow.Remixes />
              <TableRow.Favorite />
              <TableRow.Duration />
              <TableRow.Actions />
            </TableRow.Root>
          )}
        </ArpTable.Table>
      </ArpTable.Root>
    )
  }
)

export const SongsTable: FC<TableProps> = observer(
  ({ isSticky, top, defaultFilters, emptyState }) => {
    const { globalPlayer } = useTreeState()
    const currentId = globalPlayer.arpMediaIndex
    return (
      <ArpTable.Root defaultFilters={defaultFilters || DEFAULT_SONGS_FILTERS}>
        <Filters.Root>
          <Filters.Genre />
          <Filters.Key />
        </Filters.Root>
        <Headers.Root>
          <Headers.Title />
          <Headers.Tags />
          <Headers.DateMinted />
          <Headers.Plays />
          <Headers.Favorite />
          <Headers.Duration />
        </Headers.Root>
        <ArpTable.Table>
          {(media) => (
            <TableRow.Root media={media} key={media.id} active={currentId === media.id}>
              <TableRow.Main />
              <TableRow.Tags />
              <TableRow.DateMinted />
              <TableRow.Plays />
              <TableRow.Favorite />
              <TableRow.Duration />
              <TableRow.Actions />
            </TableRow.Root>
          )}
        </ArpTable.Table>
      </ArpTable.Root>
    )
  }
)
